import { Button } from "@nimit9/signet-ui/components/button"
import { Card, CardContent } from "@nimit9/signet-ui/components/card"
import { createFileRoute } from "@tanstack/react-router"
import { Loader2, Lock } from "lucide-react"
import { useState } from "react"
import { z } from "zod"
import { DevSignInPanel } from "@/components/auth/dev-sign-in-panel"
import { PaisaMark } from "@/components/common/paisa-mark"

export const Route = createFileRoute("/login")({
  validateSearch: z.object({ redirect: z.string().optional(), error: z.string().optional() }),
  component: LoginPage,
})

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <path
        d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615Z"
        fill="#4285F4"
      />
      <path
        d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18Z"
        fill="#34A853"
      />
      <path
        d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.997 8.997 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332Z"
        fill="#FBBC05"
      />
      <path
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58Z"
        fill="#EA4335"
      />
    </svg>
  )
}

// Better Auth sends a sign-in refused by the sign-up cap back as ?error=<code>.
const SIGNUP_FULL_ERRORS = new Set(["signup_full", "unable_to_create_user"])

function LoginPage() {
  const { redirect, error } = Route.useSearch()
  const [loading, setLoading] = useState(false)

  function signInWithGoogle() {
    setLoading(true)
    const callbackURL = redirect
      ? `${window.location.origin}${redirect}`
      : `${window.location.origin}/`
    fetch("/api/auth/sign-in/social", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        provider: "google",
        callbackURL,
        errorCallbackURL: `${window.location.origin}/login`,
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data?.url) window.location.href = data.url
      })
      .catch(() => setLoading(false))
  }

  return (
    <div className="relative flex min-h-dvh items-center justify-center overflow-hidden bg-bg-base px-4 py-10">
      {/* Subtle accent glow behind the card (decorative) */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute left-1/2 top-1/3 aspect-square w-[min(36rem,120vw)] -translate-x-1/2 -translate-y-1/2 rounded-full bg-accent-green/5 blur-3xl" />
      </div>

      <div className="relative flex w-full min-w-0 max-w-sm flex-col items-center gap-8">
        {/* Bars mark + headline */}
        <div className="flex flex-col items-center gap-4 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-accent-green/10">
            <PaisaMark size={30} decorative />
          </span>
          <div className="space-y-1.5">
            <h1 aria-label="Paisa" className="flex justify-center">
              <PaisaMark variant="wordmark" size={30} decorative />
            </h1>
            <p className="text-callout text-text-secondary">Personal finance, privately.</p>
          </div>
        </div>

        {/* Sign in card */}
        <Card className="w-full min-w-0">
          <CardContent className="min-w-0 space-y-5 p-4 min-[420px]:p-6 sm:p-8">
            <Button
              type="button"
              size="lg"
              onClick={signInWithGoogle}
              disabled={loading}
              className="relative h-11 w-full gap-3 rounded-full text-callout"
            >
              <GoogleIcon />
              Continue with Google
              {loading && (
                <Loader2
                  aria-label="Signing in"
                  className="absolute right-4 animate-spin text-primary-foreground/70"
                />
              )}
            </Button>

            {error && (
              <div className="space-y-3 text-center">
                <p role="alert" className="break-words text-body font-medium text-text-primary">
                  {SIGNUP_FULL_ERRORS.has(error.toLowerCase())
                    ? "We are full for now. Join the waitlist and we will let you in soon."
                    : "Sign in didn't work. Try again."}
                </p>
                {SIGNUP_FULL_ERRORS.has(error.toLowerCase()) && (
                  <Button
                    variant="outline"
                    size="lg"
                    nativeButton={false}
                    render={
                      // biome-ignore lint/a11y/useAnchorContent: Button injects its children into this anchor
                      <a href="https://trackpaisa.app/#waitlist" />
                    }
                    className="h-11 w-full rounded-full"
                  >
                    Join the waitlist
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {import.meta.env.DEV && <DevSignInPanel redirect={redirect} />}

        {/* Footer */}
        <p className="flex items-center gap-1.5 text-caption text-text-muted">
          <Lock aria-hidden="true" className="h-3 w-3" />
          Your data is encrypted.
        </p>
      </div>
    </div>
  )
}
